import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output, ViewEncapsulation } from '@angular/core';
import { CatalogueCode, Category, Code, CodeType, CodeUtility, GetCodeCategoryEntitiesRequest, PagingOptions, SubCategory, ToEmbeddedCode } from '@jms/model';
import { ObservableInput } from '@jms/utils/util.redux';
import { StateComponent } from '@jms/utils/util.state.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, of, Observable, lastValueFrom, from } from 'rxjs';
import { switchMap, tap, startWith, debounce, debounceTime } from 'rxjs/operators';
import { CatagoryChangedEventArgs } from '../../model';
import { CODE_PICKER_SERVICE } from '@jms/ui/ui.code.picker.pro';
import { CodePickerService } from '@jms/ui/ui.standalone.services/code.picker.service';
import * as Enumerable from 'linq';


export interface CodePickerVM {

    categories: Category[];
    selectedCategory: Category;
    selectedSubcategory: SubCategory;
    search: string;
    isEmpty: boolean;
    codes: CatalogueCode[];
    selectedCode: CatalogueCode;
}

const defaultState = {
    categories: [],
    selectedCategory: null,
    selectedSubcategory: null,
    search: null,
    isEmpty: false,
    codes: [],
    selectedCode: null,
} as CodePickerVM;


@UntilDestroy()
@Component({
    selector: 'code-picker',
    templateUrl: 'code.picker.component.html',
    styleUrls: ['code.picker.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        CodePickerService
    ],
    standalone: false
})
export class CodePickerComponent extends StateComponent<CodePickerVM> {

    /// This need to be set to try inside settings codes when picking child codes
    @ObservableInput() @Input('excludePricebook') public excludePricebook$: Observable<string>;
    @ObservableInput() @Input('isCodeItem') public isCodeItem$: Observable<boolean>;
    @ObservableInput() @Input('excludeTypes') public excludeTypes$: Observable<CodeType[]>;
    @ObservableInput() @Input('isPurchased') public isPurchased$: Observable<boolean>;
    @ObservableInput() @Input('isSold') public isSold$: Observable<boolean>;

    @Output() public add = new EventEmitter<any>();
    @Output() public cancel = new EventEmitter<any>();
    @Output() public addExpanded = new EventEmitter<any>();

    @Output() selected = new EventEmitter<CatalogueCode>();

    // Item paging stuff    
    private codeCurrentPage: number = null;
    private canLoadMoreItems = true;
    private isLoading = false;
    private pageSize = 100;

    private excludePricebook: string;
    private isCodeItem: boolean;

    constructor(
        @Inject(CODE_PICKER_SERVICE) private codePickerService: CodePickerService,
    ) {
        super(defaultState);

        const category$ = this.createSelector((x) => x.selectedCategory);
        const subcategory$ = this.createSelector((x) => x.selectedSubcategory);
        const search$ = this.createSelector((x) => x.search);

        const codes$ = combineLatest([
            category$,
            subcategory$,
            search$,
            this.excludePricebook$,
            this.isCodeItem$.pipe(startWith(false)),
            this.excludeTypes$,
            this.isSold$,
            this.isPurchased$
        ])
            .pipe(
                switchMap(([category, subcategory, search, excludePricebook, isCodeItem, excludeTypes, isSold, isPurchased]) => {

                    let codes: Observable<CatalogueCode[]>;
                    this.excludePricebook = excludePricebook;
                    this.isCodeItem = isCodeItem;

                    if (search) {
                        codes = this.codePickerService.searchCodes(search, isCodeItem, excludeTypes, !!isPurchased)
                    } else if (!subcategory || !category)
                        codes = of([] as CatalogueCode[]);
                    else {
                        const path = this.getPath();

                        const request: GetCodeCategoryEntitiesRequest = {
                            category: path,
                            isCodeItem: isCodeItem,
                            excludePricebook: excludePricebook,
                            excludeTypes: excludeTypes,
                            paging: this.getPaging(),
                            isSold: !!isSold,
                            isPurchased: !!isPurchased
                        };

                        codes = this.codePickerService.loadBySubCategory(request);
                    }

                    return codes;

                }),

            )

        const categories$ = from(this.isPurchased$)
            .pipe(
                debounceTime(0),
                switchMap(isPurchased => {
                    return this.codePickerService.loadCategories(isPurchased);
                })
            );

        combineLatest([categories$, codes$,])
            .pipe(
                untilDestroyed(this),
                tap(([categories, codes]) => {
                    this.setState({
                        categories: categories,
                        codes: codes,
                        isEmpty: this.vm.search
                            ? !codes?.length
                            : false
                    })
                })
            )
            .subscribe();

    }

    public onCodeSelected(code: Code) {
        this.setState(
            {
                selectedCode: code,
            }
        );
        this.selected.emit(code);
    }

    public onCategoryChanged(eventArgs: CatagoryChangedEventArgs) {
        this.resetPaging();
        this.setState({
            selectedCategory: eventArgs?.category,
            selectedSubcategory: eventArgs?.subcategory,
            selectedCode: null,
            //codes: [],
        })
    }

    public onSearch(search: string) {
        this.resetPaging();
        this.setState({
            search: search == '' ? null : search,
            selectedCategory: null,
            selectedSubcategory: null,
            selectedCode: null,
            //codes: [],
        });
    }

    public onSelectCode(code: CatalogueCode) {
        this.setState({
            selectedCode: code
        });
    }

    public async onAddCode() {

        const codes = await lastValueFrom(this.codePickerService.loadCodePickerDetails(this.vm.selectedCode?._id));

        const expandedCodes = Enumerable.from(codes)
            .selectMany(code => CodeUtility.expandUploadedCode(ToEmbeddedCode(code)))
            .toArray();

        this.add.emit(this.vm.selectedCode);
        this.addExpanded.emit(expandedCodes);

    }

    public onCancel() {
        this.cancel.emit();
    }

    public async onLoadMoreItems() {
        if (!this.canLoadMoreItems || this.isLoading) {
            return;
        }

        if (this.vm?.codes?.length < this.pageSize) {
            return;
        }

        console.log("load item");

        this.isLoading = true;

        const request: GetCodeCategoryEntitiesRequest = {
            category: this.getPath(),
            isCodeItem: this.isCodeItem,
            excludePricebook: this.excludePricebook,
            excludeTypes: null,
            paging: this.getPaging()
        };

        const codes = await lastValueFrom(this.codePickerService.loadBySubCategory(request));

        if (codes.length < this.pageSize) {
            this.canLoadMoreItems = false;
        }

        const allCodes = [...this.vm.codes, ...codes];
        this.setState({
            codes: allCodes
        });

        this.isLoading = false;
    }

    private getPaging(): PagingOptions {
        if (!this.codeCurrentPage && this.codeCurrentPage !== 0) {
            this.codeCurrentPage = 0;
        } else {
            this.codeCurrentPage++;
        }

        return {
            currentPage: this.codeCurrentPage,
            pageSize: this.pageSize,
        } as PagingOptions;
    }

    private resetPaging() {
        this.codeCurrentPage = null;
        this.canLoadMoreItems = true;
    }

    private getPath(): string {
        const category = this.vm.selectedCategory;
        const subcategory = this.vm.selectedSubcategory;

        return `${category?.name ?? ''} | ${subcategory?.name ?? ''}`;
    }
}
